/**
 * Two small rules of the Memory control (D340 phase 3). No DOM.
 *
 * Legend focus: pressing a layer or a topic in the legend lights that group and steps the rest back; pressing it again
 * clears it (the source's legend chips, brain/_core.js `legend` / `focusGroup`).
 *
 * Wheel target: the wheel zooms the picture only when it is over the picture itself. Over a list that scrolls (Find's
 * results, the card's connections, a shelf, the viewer sheet, the Set up drawer) it scrolls that list, and nothing under
 * the picture ever scrolls with the page: the Memory page is not a scroll container.
 */
import { ARMS_THEMES, type ThemeId } from './armsGroupingModel.js';
import type { Globe, GlobeLayer } from './memoryGlobeModel.js';

export interface GroupFocus {
  /** A layer (nine tiers), or absent when a whole theme is focused (five themes). */
  layer?: GlobeLayer;
  /** A theme and all its tiers. */
  theme?: ThemeId;
  /** A department of memory (an index into `Globe.departments`); absent for the whole layer. */
  dept?: number;
}

/** The focus after pressing `next`: the same group again clears it. */
export function toggleFocus(current: GroupFocus | null, next: GroupFocus): GroupFocus | null {
  return current && current.layer === next.layer && current.theme === next.theme && current.dept === next.dept ? null : next;
}

/** The nodes a focus holds (hubs and folders included), as indices; null for no focus. */
export function focusSet(globe: Globe, focus: GroupFocus | null): ReadonlySet<number> | null {
  if (!focus) return null;
  const out = new Set<number>();
  const tiers = focus.theme ? (ARMS_THEMES.find((t) => t.id === focus.theme)?.tiers ?? []) : focus.layer ? [focus.layer] : [];
  for (const n of globe.nodes) {
    if (!tiers.includes(n.layer)) continue;
    if (focus.dept !== undefined && n.deptIndex !== focus.dept) continue;
    if (focus.dept !== undefined && n.struct === 'layer') continue;
    out.add(n.index);
  }
  return out;
}

/** Whether a focus is the one shown by a legend entry. */
export function isFocused(current: GroupFocus | null, entry: GroupFocus): boolean {
  return !!current && current.layer === entry.layer && current.theme === entry.theme && current.dept === entry.dept;
}

/** The part of an element the rule needs: `closest`, as the DOM has it. */
export interface WheelTarget {
  closest: (selector: string) => unknown;
}

/** What scrolls inside the control: a list marks itself with this attribute. */
export const SCROLLS_ATTRIBUTE = 'data-memory-scroll';

/** True when the wheel over `target` zooms the picture: it is on the picture and not on anything that scrolls. */
export function wheelZoomsPicture(target: WheelTarget | null | undefined): boolean {
  if (!target) return false;
  if (target.closest(`[${SCROLLS_ATTRIBUTE}]`)) return false;
  return !!target.closest('[data-memory-brain-canvas]');
}
